
import type { ResultScoreDetails } from '@empathybroker/tsclient-play/dist/models/search/result'
import type { Result } from '@empathyco/x-types'
import type { PropType } from 'vue'
import { RootScoreCriteriaTypeEnum } from '@empathybroker/tsclient-play/dist/enums/search/debug/result/score-criteria-detail-type.enum'

import { defineComponent } from 'vue'

import { ExplainUtils } from '../../utils'

import { EpbBadge } from '../shared/epb-badge'

export default defineComponent({
  name: 'ExplainResultIconsList',
  components: {
    EpbBadge,
  },
  props: {
    result: {
      type: Object as PropType<Result>,
      required: true,
    },
    messages: {
      type: Object, // ExplainMessages
      required: true,
    },
  },
  data() {
    return {
      baseClass: 'epb-explain-result-icons-list',
    }
  },
  computed: {
    scoreCriteriaDetailTypes(): (RootScoreCriteriaTypeEnum | string)[] {
      const scoreCriteriaDetailTypes = []
      const resultScoreCriteria = ExplainUtils.getResultScoreCriteria(this.result as Result)
      if (resultScoreCriteria) {
        Object.entries(resultScoreCriteria.details).forEach(
          ([criteriaDetailType, criteriaDetail]: [
            RootScoreCriteriaTypeEnum,
            ResultScoreDetails,
          ]) => {
            if (criteriaDetail.value) {
              scoreCriteriaDetailTypes.push(criteriaDetailType)
            }
          },
        )
      }

      const sortItems = (currentValue: RootScoreCriteriaTypeEnum | string): number => {
        if (currentValue === RootScoreCriteriaTypeEnum.QUERY_RANKING) {
          return -1
        }

        return 0
      }

      return scoreCriteriaDetailTypes.sort(sortItems)
    },
    calculateWithNormalResultValue(): boolean {
      const lastResult = this.$x.results[this.$x.results.length - 1]
      const firstResult = this.$x.results[0]
      const minPercentage = 1
      const differenceInPercentage =
        (this.getDecimalResultTotalScoreValue(lastResult) * 100) /
        this.getDecimalResultTotalScoreValue(firstResult)

      return differenceInPercentage > minPercentage
    },
    maxTotalScoreValue(): number {
      return Math.max(...(this.$x.results.map(this.getResultTotalScoreValue) as number[]))
    },
  },
  methods: {
    getValueToShow(number: number): number {
      return this.calculateWithNormalResultValue ? number : Math.log1p(number)
    },
    getScoreCriteriaDetailLabel(criteriaDetailType: RootScoreCriteriaTypeEnum | string): string {
      const explainScoreCriteriaMessages = ExplainUtils.getResultScoreCriteriaDetailMessages(
        criteriaDetailType,
        this.messages.score,
      )
      return explainScoreCriteriaMessages.label
    },
    getResultScoreCriteriaDetailColor(
      criteriaDetailType: RootScoreCriteriaTypeEnum | string,
    ): string {
      return criteriaDetailType
    },
    getResultScoreCriteriaDetailIcon(
      criteriaDetailType: RootScoreCriteriaTypeEnum | string,
    ): string {
      return ExplainUtils.getResultScoreCriteriaDetailIcon(criteriaDetailType)
    },
    getCriteriaBarWidth(criteriaDetailType: RootScoreCriteriaTypeEnum | string): string {
      const resultScoreCriteria = ExplainUtils.getResultScoreCriteria(this.result as Result)

      const logTotalValueReducer = (total, criteria): number =>
        criteria.value ? total + this.getValueToShow(criteria.value) : total
      const logTotalValue: number = Object.values(resultScoreCriteria.details).reduce(
        logTotalValueReducer,
        0,
      )
      const value = this.getValueToShow(resultScoreCriteria.details[criteriaDetailType].value)
      return `${
        (((100 * value) / logTotalValue) *
          this.getPercentageOfMaxValue(this.getValueToShow(resultScoreCriteria.value))) /
        100
      }%`
    },
    getPercentageOfMaxValue(value: number): number {
      return (value / this.maxTotalScoreValue) * 100
    },
    getResultTotalScoreValue(result: Result): number {
      const value = this.getDecimalResultTotalScoreValue(result)

      return this.getValueToShow(value)
    },
    getDecimalResultTotalScoreValue(result: Result): number {
      return Number(ExplainUtils.getResultScoreCriteria(result).value ?? 0)
    },
  },
})
